import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";

import EPG_DAY_RANGE_DEFAULTS, {
  EPG_REANCHOR_AFTER_BACKGROUND_MS,
} from "$src/core/constants/epg-day-range";
import { HOUR } from "$src/core/constants/time";
import useApi from "$src/core/hooks/use-api";
import useAppStateEffect from "$src/core/hooks/use-app-state-effect";
import type { EPGConfigDto } from "$src/core/models/dto";
import Logging from "$src/core/services/logging/logging";

const queryKey = (daysBefore: number, daysAfter: number, timeZone: string) => [
  "epg",
  daysBefore,
  daysAfter,
  timeZone,
  "artwork",
];

const STALE_TIME_30_MIN = HOUR / 2;
const CACHE_TIME_5H = HOUR * 5;

type UseEPGQueryOptions = {
  isEnabled?: boolean;
  epgConfig?: EPGConfigDto;
  timeZone: string;
};

const useEPGQuery = (options: UseEPGQueryOptions) => {
  const { api } = useApi();
  const daysBefore =
    options.epgConfig?.daysBefore ?? EPG_DAY_RANGE_DEFAULTS.daysBefore;
  const daysAfter =
    options.epgConfig?.daysAfter ?? EPG_DAY_RANGE_DEFAULTS.daysAfter;

  const isEnabled = (options.isEnabled ?? true) && options.epgConfig != null;

  const query = useQuery({
    queryKey: queryKey(daysBefore, daysAfter, options.timeZone),
    queryFn: () =>
      api.getEPG({
        daysBefore,
        daysAfter,
        timeZone: options.timeZone,
      }),
    staleTime: STALE_TIME_30_MIN,
    gcTime: CACHE_TIME_5H,
    enabled: isEnabled,
  });

  const { refetch } = query;
  const hiddenAtMsRef = useRef<number | null>(null);

  /* react-query only refetches on focus on web, so a long background leaves a stale schedule here. */
  useAppStateEffect(
    (state) => {
      if (state !== "active") {
        hiddenAtMsRef.current = Date.now();
        return;
      }

      const hiddenAtMs = hiddenAtMsRef.current;
      hiddenAtMsRef.current = null;

      if (!isEnabled || hiddenAtMs == null) return;
      if (Date.now() - hiddenAtMs < EPG_REANCHOR_AFTER_BACKGROUND_MS) return;

      refetch().catch((error: unknown) => {
        Logging.error("Could not refresh the EPG after foregrounding", {
          error,
        });
      });
    },
    [isEnabled, refetch],
  );

  return query;
};

export default useEPGQuery;
